<template>
    <modal
        :show.sync="showModal"
        body-classes="p-0"
        dismissable
        :modal-classes="`modal-xl ${$style.modalClasses}`"
    >
        <card class="card-form mb-0">
            <template slot="header">
                <div class="flex-between">
                    <h2 class="modal-title">{{ title }}</h2>
                    <div class="flex-end">
                        <base-button
                            type="info"
                            size="sm"
                            class="btn-fill mr-4"
                            @click="printPage"
                        >
                            <i class="fa fa-print mr-1" />
                            Print page
                        </base-button>
                        <button
                            type="button"
                            class="close"
                            aria-label="Close"
                            @click="reset()"
                        >
                            <i class="tim-icons icon-simple-remove" />
                        </button>
                    </div>
                </div>
                <h4 v-if="!isWillCall" :class="$style.advanceNotice">
                    * Delivery requests must be placed at least 48 hours before
                    your desired delivery date.
                </h4>
                <div class="mt-2">
                    <p><strong>Submitted:</strong> {{ submittedDate }}</p>
                    <span
                        v-if="delivery.status"
                        :class="getStyleForStatus(delivery.status)"
                    >
                        {{ delivery.status || "--" }}
                    </span>
                    <p v-else>Status: --</p>
                </div>
            </template>
            <div :class="$style.header">
                <div :class="$style.customer">
                    <div :class="$style.label">Customer</div>
                    <div :class="$style.title">
                        {{ customerName }}
                    </div>
                </div>
                <div :class="$style.designer">
                    <div :class="$style.label">Designer</div>
                    <div :class="$style.title">
                        {{ designerName }}
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-12">
                    <el-table
                        v-if="tableItems.length"
                        :class="$style.table"
                        max-height="350"
                        :data="tableItems"
                    >
                        <el-table-column
                            v-for="column in tableColumns"
                            :key="column.label"
                            :width="column.width"
                            :min-width="column.minWidth"
                            :prop="column.prop"
                            :label="column.label"
                            :formatter="column.formatter"
                            :show-overflow-tooltip="true"
                        />
                    </el-table>
                </div>
            </div>
            <form
                class="mt-3"
                autocomplete="off"
                @submit.prevent="validate().then(updateDeliveryRequest)"
            >
                <div class="row">
                    <div v-if="!isWillCall" class="col-4">
                        <base-input
                            v-model="delivery.locationName"
                            name="location"
                            type="text"
                            label="Location Name"
                            disabled
                        />
                    </div>
                    <div class="col-4">
                        <base-input
                            name="deliveryDate"
                            type="date"
                            :label="deliveryDateLabel"
                        >
                            <el-date-picker
                                v-model="delivery.deliveryDate"
                                format="MM/dd/yyyy"
                                type="date"
                                placeholder="Select date"
                                disabled
                            />
                        </base-input>
                    </div>
                    <div class="col-4">
                        <base-input
                            name="deliveryWindow"
                            type="text"
                            :label="deliveryWindowLabel"
                            :value="deliveryWindowReadOnlyLabel"
                            disabled
                        />
                    </div>
                </div>
                <div class="row">
                    <div class="col-4">
                        <base-input
                            name="jobSiteContactName"
                            type="text"
                            v-model="delivery.contacts[0].name"
                            :label="contactNameLabel"
                            disabled
                        />
                    </div>
                    <div class="col-4">
                        <base-input
                            :label="contactPhoneLabel"
                            name="jobSiteContactPhone"
                            v-model="delivery.contacts[0].phone"
                            type="text"
                            disabled
                        />
                    </div>
                    <div class="col-4">
                        <base-input
                            name="jobSiteContactEmail"
                            type="text"
                            v-model="delivery.contacts[0].email"
                            :label="contactEmailLabel"
                            disabled
                        />
                    </div>
                </div>
                <div v-if="!isWillCall" class="row">
                    <div class="col-4">
                        <base-input
                            name="address"
                            type="text"
                            v-model="delivery.deliveryAddress"
                            label="Delivery Address"
                            disabled
                        />
                    </div>
                    <div class="col-4">
                        <base-input
                            name="city"
                            type="text"
                            v-model="delivery.deliveryCity"
                            label="Delivery City"
                            disabled
                        />
                    </div>
                    <div class="col-4">
                        <base-input
                            label="Delivery Zip"
                            name="zip"
                            autocomplete="off"
                            v-model="delivery.deliveryZip"
                            type="text"
                            disabled
                        />
                    </div>
                </div>
                <div v-if="!isWillCall" class="row">
                    <div class="col-4">
                        <base-input
                            name="deliverInto"
                            type="text"
                            label="Deliver Into"
                            :value="delivery.deliverInto"
                            disabled
                        />
                    </div>
                    <div class="col-2">
                        <base-input
                            name="elevator"
                            type="text"
                            label="Elevator?"
                            :value="delivery.hasElevator ? 'Yes' : 'No'"
                            disabled
                        />
                    </div>
                    <div class="col-3">
                        <base-input
                            name="intStairs"
                            type="text"
                            label="# of Interior Stairs"
                            :value="delivery.interiorStairsCount || 0"
                            disabled
                        />
                    </div>
                    <div class="col-3">
                        <base-input
                            name="extStairs"
                            type="text"
                            label="# of Exterior Stairs"
                            :value="delivery.exteriorStairsCount || 0"
                            disabled
                        />
                    </div>
                </div>
                <div
                    v-for="(contact, index) in additionalContacts"
                    class="row"
                    :key="index"
                >
                    <div class="col-4">
                        <base-input
                            v-focus
                            focus
                            :name="`jobSiteContactName${index + 1}`"
                            type="text"
                            :label="`${contactNameLabel} ${index + 1}`"
                            v-model="delivery.contacts[index + 1].name"
                            disabled
                        />
                    </div>
                    <div class="col-4">
                        <base-input
                            :label="`${contactPhoneLabel} ${index + 1}`"
                            :name="`jobSiteContactPhone${index + 1}`"
                            autocomplete="off"
                            type="text"
                            v-model="delivery.contacts[index + 1].phone"
                            placeholder="(123) 456-7890"
                            disabled
                        />
                    </div>
                    <div class="col-4">
                        <div class="form-group has-label">
                            <base-input
                                :name="`jobSiteContactEmail${index + 1}`"
                                type="text"
                                :label="`${contactEmailLabel} ${index + 1}`"
                                v-model="delivery.contacts[index + 1].email"
                                disabled
                            />
                        </div>
                    </div>
                </div>
                <div>
                    <base-input
                        name="deliveryNotes"
                        :label="deliveryNotesLabel"
                    >
                        <textarea
                            v-focus
                            v-model="delivery.deliveryNotes"
                            style="max-height: unset"
                            class="form-control"
                            rows="3"
                            disabled
                        />
                    </base-input>
                </div>
                <div v-if="!isWillCall">
                    <p class="small">
                        * Please be sure to include in the Special Instructions
                        if your delivery requires a special sized truck; if the
                        site is difficult to access, or has steep or narrow
                        roads and/or driveways; if there is a long carry; if we
                        need a gate or door code, etc. Please note that these
                        conditions may cause delays in the delivery and may
                        increase the time we spend on site which could result in
                        additional charges.
                    </p>
                    <p class="small">
                        * We no longer do 3rd party billing. We will bill you
                        for the delivery -- if you would like your customer to
                        pay for the delivery, we are happy to accept a check
                        from your customer or you may call us with their credit
                        card information. All new accounts will be required to
                        have a credit card on file for a minimum of 5 deliveries
                        at which time we will evaluate the possibility of an
                        open account.
                    </p>
                </div>
                <el-divider />
                <div class="d-flex">
                    <i
                        v-if="delivery.agreedToTerms"
                        class="far fa-check-square"
                        style="line-height: unset"
                    />
                    <i v-else class="fa fa-times" style="line-height: unset" />
                    <span class="ml-2 d-flex">
                        I have read and agree to
                        <span v-if="!isWillCall" class="ml-1 mr-1">
                            the above and to </span
                        >the
                        <base-button
                            v-if="materialType === 'Cabinets'"
                            link
                            :class="$style.link"
                            type="primary"
                            @click="showCabinetTermsAndConditions = true"
                        >
                            terms and conditions.
                        </base-button>
                        <base-button
                            v-else-if="materialType === 'Furniture'"
                            link
                            :class="$style.link"
                            type="primary"
                            @click="showFurnitureTermsAndConditions = true"
                        >
                            terms and conditions.
                        </base-button>
                        <base-button
                            v-else
                            link
                            :class="$style.link"
                            @click="
                                showCabinetTermsAndConditions = true;
                                showFurnitureTermsAndConditions = true;
                            "
                            type="primary"
                        >
                            terms and conditions.
                        </base-button>
                    </span>
                    <label class="error">{{ errors[0] }}</label>
                </div>
                <div class="d-flex mt-2">
                    <a href="/docs/baserates.pdf">
                        Download & view current city/zip code rates.
                    </a>
                </div>
                <div :class="$style.actions">
                    <base-button
                        type="default"
                        class="btn-fill"
                        @click="reset()"
                    >
                        Close
                    </base-button>
                    <div :class="$style.right">
                        <base-button
                            type="primary"
                            class="btn-fill"
                            @click="
                                $emit('update:showChangeRequestModal', true)
                            "
                        >
                            Request Change
                        </base-button>
                        <base-button
                            v-if="showUpdateButton"
                            type="info"
                            class="btn-fill"
                            @click="$emit('update:showUpdateModal', true)"
                        >
                            Update
                        </base-button>
                    </div>
                </div>
            </form>
        </card>
        <cabinet-terms-and-conditions-modal
            :show.sync="showCabinetTermsAndConditions"
        />
        <furniture-terms-and-conditions-modal
            :show.sync="showFurnitureTermsAndConditions"
        />

        <delivery-request-update-modal
            :show="showUpdateModal"
            :delivery-request="delivery"
            @update:show="$emit('update:showUpdateModal', $event)"
            @updateDeliveryRequest="$emit('updateDeliveryRequest', $event)"
        />
        <delivery-change-request-modal
            :show="showChangeRequestModal"
            :delivery-request="delivery"
            @update:show="$emit('update:showChangeRequestModal', $event)"
            @submitChangeRequest="$emit('submitChangeRequest', $event)"
        />
    </modal>
</template>

<script>
import moment from "moment-timezone";
import { Modal } from "src/components/UI";
import { DatePicker, Divider } from "element-ui";
import DeliveryRequestUpdateModal from "./DeliveryRequestUpdateModal";
import DeliveryChangeRequestModal from "./DeliveryChangeRequestModal";
import CabinetTermsAndConditionsModal from "../../NewDeliveryRequest/_components/CabinetTermsAndConditionsModal";
import FurnitureTermsAndConditionsModal from "../../NewDeliveryRequest/_components/FurnitureTermsAndConditionsModal";

export default {
    name: "DeliveryRequestDetail",
    components: {
        Modal,
        [DatePicker.name]: DatePicker,
        [Divider.name]: Divider,
        DeliveryRequestUpdateModal,
        DeliveryChangeRequestModal,
        CabinetTermsAndConditionsModal,
        FurnitureTermsAndConditionsModal,
    },
    props: {
        delivery: {
            type: Object,
            required: true,
        },
        show: {
            type: Boolean,
            required: true,
        },
        showUpdateButton: {
            type: Boolean,
            required: true,
        },
        showUpdateModal: {
            type: Boolean,
            required: true,
        },
        showChangeRequestModal: {
            type: Boolean,
            required: true,
        },
        requestDeliveryType: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            showRequestChangeModal: false,
            showCabinetTermsAndConditions: false,
            showFurnitureTermsAndConditions: false,
            deliveryWindowOptions: [
                { value: "Morning", label: "9AM - 11AM" },
                { value: "Midday", label: "11AM - 1PM" },
                { value: "Afternoon", label: "1PM - 3PM" },
                { value: "Any", label: "Any" },
            ],
            showModal: false,
            tableColumns: [
                {
                    prop: "receiptLogId",
                    label: "Receipt Log ID",
                    width: 120,
                },
                {
                    prop: "unitId",
                    label: "Unit ID",
                    width: 120,
                },
                {
                    prop: "manufacturer.name",
                    label: "Manufacturer",
                },
                {
                    prop: "purchaseOrder",
                    label: "P.O. No.",
                    formatter: (row) =>
                        this.receiptLogs.find(
                            (log) => log.id === row.receiptLogId,
                        ).purchaseOrder,
                },
                {
                    prop: "description",
                    label: "Description",
                },
                {
                    prop: "quantity",
                    label: "Cubic Ft. / Items",
                },
            ],
        };
    },
    computed: {
        isWillCall() {
            return this.delivery.willCall;
        },
        receiptLogs() {
            return this.delivery.deliveryRequestDetails.map((detail) => ({
                ...detail.receiptLog,
                designer: detail.designer,
            }));
        },
        submittedDate() {
            if (this.delivery.createdAt) {
                return moment(this.delivery.createdAt).format("MM/DD/YYYY");
            }
            return "--";
        },
        deliveryWindowReadOnlyLabel() {
            const option = this.deliveryWindowOptions.find(
                (o) => o.value === this.delivery.deliveryWindow,
            );
            return option?.label;
        },
        title() {
            return this.isWillCall
                ? `Will Call #${this.delivery.id}`
                : `Delivery Request #${this.delivery.id}`;
        },
        deliveryDateLabel() {
            return this.isWillCall
                ? "Desired Pickup Date"
                : "Desired Delivery Date";
        },
        deliveryWindowLabel() {
            return this.isWillCall
                ? "Desired Pickup Window"
                : "Desired Delivery Window";
        },
        contactNameLabel() {
            return this.isWillCall ? "Contact Name" : "Job Site Contact Name";
        },
        contactPhoneLabel() {
            return this.isWillCall ? "Contact Phone" : "Job Site Contact Phone";
        },
        contactEmailLabel() {
            return this.isWillCall ? "Contact Email" : "Job Site Contact Email";
        },
        deliveryNotesLabel() {
            return this.isWillCall
                ? "Pick up Notes/Special Instructions"
                : "Delivery Notes/Special Instructions";
        },
        customerName() {
            return this.delivery.customer.name;
        },
        additionalContacts() {
            // exclude first result
            return this.delivery.contacts.slice(1);
        },
        designerName() {
            if (this.receiptLogs.length > 1) {
                const designers = new Set(
                    this.receiptLogs.map((log) => log.designer?.fullName),
                );
                if (designers.size > 1) return "Multiple";
                return designers.values().next().value;
            }
            return this.receiptLogs[0].designer?.fullName || "N/A";
        },
        tableItems() {
            return this.receiptLogs.map((log) => log.units).flat();
        },
        materialType() {
            const allTypes = this.receiptLogs.map(
                (log) => log.materialType.description,
            );
            const types = [...new Set(allTypes)];
            if (types.includes("Cabinets") && types.includes("Furniture")) {
                return "Both";
            }
            if (types.includes("Cabinets")) return "Cabinets";
            if (types.includes("Furniture")) return "Furniture";

            return "Unknown";
        },
    },
    watch: {
        show: {
            immediate: true,
            handler(val) {
                this.showModal = val;
            },
        },
        showModal(val) {
            this.$emit("update:show", val);
        },
    },
    methods: {
        getStyleForStatus(status) {
            if (!status) return "";
            return [status.toLowerCase(), "statusLabel"];
        },
        printPage() {
            window.print();
        },
        reset() {
            this.showModal = false;
            this.$emit("cancel");
        },
    },
};
</script>

<style module lang="scss">
@import "assets/sass/dashboard/custom/_variables.scss";

.modalClasses {
    min-width: 750px;
}

.header {
    display: flex;
    padding-left: 10px;
    padding-right: 30px;

    .title {
        font-size: 18px;
        color: $black;
        font-weight: 600;
    }

    .customer {
        flex-basis: 70%;
    }
}

.actions {
    margin-top: 30px;
    display: flex;
    justify-content: space-between;
}

.actions .right > * {
    margin-right: 10px;
    margin-left: 10px;
}

.link {
    font-weight: 400;
    padding: 0px;
    margin: 0px;
    margin-left: 3px;
    line-height: 1.5;
}

.advanceNotice {
    margin-top: 20px;
    font-weight: bold;
    color: hsla(349, 72%, 48%, 1) !important;
}

@media print {
    button {
        display: none !important;
    }

    * {
        overflow: visible;
        text-overflow: unset;
    }

    .modalClasses {
        max-width: 100% !important;
        width: 100% !important;
        padding: 0 20px;
    }

    .table {
        max-width: 1200px;
    }
}
</style>
